import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { patientService } from '../services/patientService';
import './PatientForm.css';

const PatientForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const fileInputRef = useRef(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    // Patient Information
    firstName: '',
    lastName: '',
    fullName: '',
    phoneNumber: '',
    email: '',
    primaryDoctor: '',
    dob: '',
    gender: '',
    bloodGroup: '',
    status: 'active',

    // Address Information
    address1: '',
    address2: '',
    country: 'United States',
    city: '',
    state: '',
    pincode: '',

    // Additional fields from backend
    age: '',
    emergencyContact: '',
    primaryLanguage: '',
    raceEthnicity: ''
  });

  // Dropdown options
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
  const bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const statusOptions = ['active', 'inactive', 'draft'];
  const raceEthnicityOptions = [
    'American Indian or Alaska Native',
    'Asian',
    'Black or African American',
    'Hispanic or Latino',
    'Native Hawaiian or Other Pacific Islander',
    'White',
    'Two or More Races',
    'Other',
    'Prefer not to say'
  ];

  // Countries and cities - United States only
  const countryOptions = ['United States'];
  const stateOptions = {
    'United States': [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
      'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
      'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
      'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
      'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
      'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
      'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
      'Wisconsin', 'Wyoming'
    ]
  };
  const cityOptions = {
    'California': ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento'],
    'Texas': ['Houston', 'Dallas', 'Austin', 'San Antonio', 'Fort Worth'],
    'New York': ['New York City', 'Buffalo', 'Rochester', 'Albany', 'Syracuse'],
    // Add more as needed
  };

  // Load patient data if in edit mode
  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchPatientData();
    }
  }, [id]);

  // Calculate age from DOB
  useEffect(() => {
    if (formData.dob) {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  }, [formData.dob]);

  // Update fullName when firstName or lastName changes
  useEffect(() => {
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    setFormData(prev => ({ ...prev, fullName }));
  }, [formData.firstName, formData.lastName]);

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      const patient = await patientService.getPatientById(id);

      // Split fullName into firstName and lastName if needed
      let firstName = '';
      let lastName = '';
      if (patient.fullName) {
        const nameParts = patient.fullName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      // Parse address if it's a single string
      let address1 = '';
      let address2 = '';
      if (patient.address) {
        const addressParts = patient.address.split(',');
        address1 = addressParts[0]?.trim() || '';
        address2 = addressParts.slice(1).join(',').trim() || '';
      }

      setFormData({
        ...formData,
        firstName,
        lastName,
        fullName: patient.fullName || '',
        phoneNumber: patient.phoneNumber || '',
        email: patient.email || '',
        primaryDoctor: patient.primaryDoctor || '',
        dob: patient.dob ? patient.dob.split('T')[0] : '',
        gender: patient.gender || '',
        bloodGroup: patient.bloodGroup || '',
        status: patient.status || 'active',
        address1,
        address2,
        country: patient.country || '',
        city: patient.city || '',
        state: patient.state || '',
        pincode: patient.pincode || '',
        age: patient.age || '',
        emergencyContact: patient.emergencyContact || '',
        primaryLanguage: patient.primaryLanguage || '',
        raceEthnicity: patient.raceEthnicity || ''
      });

      // Set profile image if exists
      if (patient.profileImage) {
        setProfilePreview(patient.profileImage);
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
      alert('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload an image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      setProfileImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

    // Email is optional, but validate format if provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    // Phone number is optional, but validate format if provided
    if (formData.phoneNumber.trim() && !/^\+?[\d\s-()]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Invalid phone number';
    }

    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }

    setSaving(true);

    try {
      // Prepare data for submission
      const patientData = {
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        dob: formData.dob,
        age: formData.age,
        gender: formData.gender,
        bloodGroup: formData.bloodGroup,
        address: formData.address1 + (formData.address2 ? ', ' + formData.address2 : '') +
                 (formData.city ? ', ' + formData.city : '') +
                 (formData.state ? ', ' + formData.state : '') +
                 (formData.country ? ', ' + formData.country : '') +
                 (formData.pincode ? ' - ' + formData.pincode : ''),
        status: formData.status,
        emergencyContact: formData.emergencyContact,
        primaryLanguage: formData.primaryLanguage,
        raceEthnicity: formData.raceEthnicity,
        // Add custom fields for new data
        customFields: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          primaryDoctor: formData.primaryDoctor,
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          pincode: formData.pincode
        }
      };

      let response;
      if (isEditMode) {
        response = await patientService.updatePatient(id, patientData);

        // Upload profile picture if selected
        if (profileImage) {
          try {
            const uploadResponse = await patientService.uploadProfilePicture(id, profileImage);
            console.log('Profile picture uploaded:', uploadResponse);
          } catch (uploadError) {
            console.error('Failed to upload profile picture:', uploadError);
            alert('Patient updated but profile picture upload failed. You can try again later.');
          }
        }

        alert('Patient updated successfully!');
      } else {
        response = await patientService.createPatient(patientData);

        // Upload profile picture if selected
        if (profileImage && response?.data?._id) {
          try {
            const uploadResponse = await patientService.uploadProfilePicture(response.data._id, profileImage);
            console.log('Profile picture uploaded:', uploadResponse);
          } catch (uploadError) {
            console.error('Failed to upload profile picture:', uploadError);
            alert('Patient created but profile picture upload failed. You can try again later.');
          }
        }

        alert('Patient created successfully!');
      }

      // Navigate to patients list
      navigate('/patients');

    } catch (error) {
      console.error('Error saving patient:', error);
      alert(error.response?.data?.message || 'Failed to save patient. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/patients');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="patient-form-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading patient data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="patient-form-container">
        <div className="form-header">
          <h1>{isEditMode ? 'Edit Patient' : 'Add New Patient'}</h1>
        </div>

        <form onSubmit={handleSubmit} className="patient-form">
          {/* Patient Information Section */}
          <div className="form-section">
            <h2 className="section-title">Patient Information</h2>

            {/* Profile Image Upload */}
            <div className="profile-upload-section">
              <label className="form-label">Profile Image</label>
              <div className="profile-upload-container">
                <div className="profile-preview">
                  {profilePreview ? (
                    <img src={profilePreview} alt="Profile" />
                  ) : (
                    <div className="profile-placeholder">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M20 20C24.42 20 28 16.42 28 12C28 7.58 24.42 4 20 4C15.58 4 12 7.58 12 12C12 16.42 15.58 20 20 20Z" fill="#E0E0E0"/>
                        <path d="M20 24C13.33 24 4 27.33 4 34V36H36V34C36 27.33 26.67 24 20 24Z" fill="#E0E0E0"/>
                      </svg>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose Image
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="First Name"
                  className={`form-input ${errors.firstName ? 'error' : ''}`}
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Last Name"
                  className={`form-input ${errors.lastName ? 'error' : ''}`}
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Phone Number
                </label>
                <div className="phone-input-wrapper">
                  <span className="phone-prefix">📞 +1</span>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="000-000-0000"
                    className={`form-input with-prefix ${errors.phoneNumber ? 'error' : ''}`}
                  />
                </div>
                {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email Address (Optional)"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Primary Doctor</label>
                <input
                  type="text"
                  name="primaryDoctor"
                  value={formData.primaryDoctor}
                  onChange={handleInputChange}
                  placeholder="Enter doctor's name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  DOB <span className="required">*</span>
                </label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className={`form-input ${errors.dob ? 'error' : ''}`}
                />
                {errors.dob && <span className="error-message">{errors.dob}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Age</label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder="Age"
                  className="form-input"
                  readOnly
                />
                <span className="text-xs text-gray-500">Auto-calculated from DOB</span>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Gender <span className="required">*</span>
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={`form-select ${errors.gender ? 'error' : ''}`}
                >
                  <option value="">Select</option>
                  {genderOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {errors.gender && <span className="error-message">{errors.gender}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Blood Group</label>
                <select
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select</option>
                  {bloodGroupOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {statusOptions.map(option => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Emergency Contact</label>
                <input
                  type="tel"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                  placeholder="Emergency Contact Number"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Primary Language</label>
                <input
                  type="text"
                  name="primaryLanguage"
                  value={formData.primaryLanguage}
                  onChange={handleInputChange}
                  placeholder="e.g., English"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Race/Ethnicity</label>
                <select
                  name="raceEthnicity"
                  value={formData.raceEthnicity}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select</option>
                  {raceEthnicityOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div className="form-section">
            <h2 className="section-title">Address Information</h2>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Address 1</label>
                <input
                  type="text"
                  name="address1"
                  value={formData.address1}
                  onChange={handleInputChange}
                  placeholder="Street Address"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address 2</label>
                <input
                  type="text"
                  name="address2"
                  value={formData.address2}
                  onChange={handleInputChange}
                  placeholder="Apartment, Suite, etc."
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Country</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select</option>
                  {countryOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">State</label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="form-select"
                  disabled={!formData.country}
                >
                  <option value="">Select</option>
                  {formData.country && stateOptions[formData.country]?.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  placeholder="Pincode"
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : (isEditMode ? 'Update Patient' : 'Save Patient')}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default PatientForm;